import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Calendar, Star, Save, X, 
  ChevronLeft, ChevronRight, Code, Clock, Tag, 
  Award, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const TAGS = ['Arrays', 'Strings', 'Dynamic Programming', 'Trees', 'Graphs', 'Math', 'Sorting', 'Recursion', 'Hash Table', 'Two Pointers', 'Binary Search', 'Greedy'];
const LANGUAGES = ['python', 'javascript', 'cpp', 'java', 'c'];

const POTDManagementAdmin = () => {
  const [problems, setProblems] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('problems'); // problems, schedule, create
  
  // Problem form state
  const [showForm, setShowForm] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    difficulty: 'easy',
    tags: [],
    constraints: '',
    input_format: '',
    output_format: '',
    time_limit: 2.0,
    memory_limit: 128000,
    test_cases: [{ input: '', expected_output: '', is_sample: true }],
    editorial: '',
    starter_code: {}
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Schedule form
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleProblemId, setScheduleProblemId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [problemsRes, scheduleRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/coding/problems`, { headers }),
        axios.get(`${API}/admin/coding/potd/schedule`, { headers }),
        axios.get(`${API}/admin/coding/stats`, { headers })
      ]);
      
      setProblems(problemsRes.data.problems || []);
      setSchedule(scheduleRes.data.schedule || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProblem = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingProblem) {
        await axios.put(`${API}/admin/coding/problems/${editingProblem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Problem updated successfully');
      } else {
        await axios.post(`${API}/admin/coding/problems`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Problem created successfully');
      }
      
      setShowForm(false);
      setEditingProblem(null);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Failed to save problem:', err);
      toast.error(err.response?.data?.detail || 'Failed to save problem');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProblem = async (problemId) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/coding/problems/${problemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Problem deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete problem');
    }
  };

  const handleSchedulePOTD = async () => {
    if (!scheduleDate || !scheduleProblemId) {
      toast.error('Please select a date and problem');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/coding/potd/schedule`, {
        scheduled_date: scheduleDate,
        problem_id: scheduleProblemId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('POTD scheduled successfully');
      setScheduleDate('');
      setScheduleProblemId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule POTD');
    }
  };

  const handleSetTodayPOTD = async (problemId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/coding/potd/set`, { problem_id: problemId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('POTD set for today');
      fetchData();
    } catch (err) {
      toast.error('Failed to set POTD');
    }
  };

  const handleDeleteSchedule = async (date) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/coding/potd/schedule/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Schedule removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      description: '',
      difficulty: 'easy',
      tags: [],
      constraints: '',
      input_format: '',
      output_format: '',
      time_limit: 2.0,
      memory_limit: 128000,
      test_cases: [{ input: '', expected_output: '', is_sample: true }],
      editorial: '',
      starter_code: {}
    });
  };

  const editProblem = (problem) => {
    setEditingProblem(problem);
    setFormData({
      title: problem.title || '',
      slug: problem.slug || '',
      description: problem.description || '',
      difficulty: problem.difficulty || 'easy',
      tags: problem.tags || [],
      constraints: problem.constraints || '',
      input_format: problem.input_format || '',
      output_format: problem.output_format || '',
      time_limit: problem.time_limit || 2.0,
      memory_limit: problem.memory_limit || 128000,
      test_cases: problem.test_cases || [{ input: '', expected_output: '', is_sample: true }],
      editorial: problem.editorial || '',
      starter_code: problem.starter_code || {}
    });
    setShowForm(true);
  };

  const addTestCase = () => {
    setFormData(prev => ({
      ...prev,
      test_cases: [...prev.test_cases, { input: '', expected_output: '', is_sample: false }]
    }));
  };

  const removeTestCase = (index) => {
    setFormData(prev => ({
      ...prev,
      test_cases: prev.test_cases.filter((_, i) => i !== index)
    }));
  };

  const updateTestCase = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      test_cases: prev.test_cases.map((tc, i) => 
        i === index ? { ...tc, [field]: value } : tc
      )
    }));
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case 'easy': return 'bg-green-100 text-green-700 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'hard': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPoints = (diff) => {
    switch (diff) {
      case 'easy': return 10;
      case 'medium': return 25;
      case 'hard': return 50;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-indigo-600" size={48} />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">POTD Management</h1>
          <p className="text-gray-600">Manage coding problems and schedule Problem of the Day</p>
        </div>
        <button
          onClick={() => { resetForm(); setEditingProblem(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
        >
          <Plus size={18} />
          Create Problem
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_problems}</div>
            <div className="text-sm text-gray-500">Total Problems</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_submissions}</div>
            <div className="text-sm text-gray-500">Total Submissions</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_participants}</div>
            <div className="text-sm text-gray-500">Active Coders</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{stats.verdict_stats?.Accepted || 0}</div>
            <div className="text-sm text-gray-500">Accepted Solutions</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('problems')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'problems' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Problems ({problems.length})
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'schedule' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          POTD Schedule ({schedule.length})
        </button>
      </div>

      {/* Problems Tab */}
      {activeTab === 'problems' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Problem</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Difficulty</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Points</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Test Cases</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {problems.map((problem) => (
                <tr key={problem.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">{problem.title}</div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {problem.tags?.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(problem.difficulty)}`}>
                      {problem.difficulty?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="font-semibold text-indigo-600">{getPoints(problem.difficulty)}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-gray-600">
                      {problem.test_cases?.filter(tc => tc.is_sample).length} sample / {problem.test_cases?.length} total
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    {problem.is_active ? (
                      <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle size={14} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
                        <AlertCircle size={14} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleSetTodayPOTD(problem.id)}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Set as Today's POTD"
                      >
                        <Star size={16} />
                      </button>
                      <button
                        onClick={() => editProblem(problem)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteProblem(problem.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <div className="space-y-6">
          {/* Schedule Form */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Schedule New POTD</h3>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Problem</label>
                <select
                  value={scheduleProblemId}
                  onChange={(e) => setScheduleProblemId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a problem...</option>
                  {problems.filter(p => p.is_active).map(p => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({p.difficulty})
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleSchedulePOTD}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                Schedule
              </button>
            </div>
          </div>

          {/* Schedule List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Scheduled POTDs</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {schedule.length > 0 ? schedule.map((item) => {
                const isToday = item.date === new Date().toISOString().split('T')[0];
                const isPast = new Date(item.date) < new Date(new Date().toISOString().split('T')[0]);
                
                return (
                  <div key={item.date} className={`px-4 py-4 flex items-center justify-between ${isToday ? 'bg-indigo-50' : ''}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        isToday ? 'bg-indigo-600 text-white' : isPast ? 'bg-gray-100 text-gray-400' : 'bg-indigo-100 text-indigo-600'
                      }`}>
                        <Calendar size={20} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {new Date(item.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                          {isToday && <span className="ml-2 text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full">TODAY</span>}
                        </div>
                        <div className="text-sm text-gray-600">{item.problem_title}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(item.problem_difficulty)}`}>
                        {item.problem_difficulty?.toUpperCase()}
                      </span>
                      {!isPast && (
                        <button
                          onClick={() => handleDeleteSchedule(item.date)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="px-4 py-12 text-center">
                  <Calendar className="mx-auto text-gray-300 mb-3" size={48} />
                  <div className="text-gray-500">No scheduled POTDs</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Problem Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingProblem ? 'Edit Problem' : 'Create New Problem'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingProblem(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateProblem} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Two Sum"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                    required
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="two-sum"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Problem description..."
                />
              </div>

              {/* Difficulty & Time Limit */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty *</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {DIFFICULTIES.map(d => (
                      <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)} ({getPoints(d)} pts)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time Limit (seconds)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="10"
                    value={formData.time_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, time_limit: parseFloat(e.target.value) }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Memory Limit (KB)</label>
                  <input
                    type="number"
                    value={formData.memory_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, memory_limit: parseInt(e.target.value) }))}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        formData.tags.includes(tag) 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Input/Output Format */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Input Format</label>
                  <textarea
                    value={formData.input_format}
                    onChange={(e) => setFormData(prev => ({ ...prev, input_format: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                  <textarea
                    value={formData.output_format}
                    onChange={(e) => setFormData(prev => ({ ...prev, output_format: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Constraints */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Constraints</label>
                <textarea
                  value={formData.constraints}
                  onChange={(e) => setFormData(prev => ({ ...prev, constraints: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="1 <= n <= 10^5"
                />
              </div>

              {/* Test Cases */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Test Cases *</label>
                  <button
                    type="button"
                    onClick={addTestCase}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    + Add Test Case
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.test_cases.map((tc, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-700">Test Case {idx + 1}</span>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={tc.is_sample}
                              onChange={(e) => updateTestCase(idx, 'is_sample', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Sample (visible to users)
                          </label>
                        </div>
                        {formData.test_cases.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTestCase(idx)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Input</label>
                          <textarea
                            value={tc.input}
                            onChange={(e) => updateTestCase(idx, 'input', e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="2 7 11 15\n9"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Expected Output</label>
                          <textarea
                            value={tc.expected_output}
                            onChange={(e) => updateTestCase(idx, 'expected_output', e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="0 1"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingProblem(null); }}
                  className="px-6 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Save size={18} />
                  )}
                  {editingProblem ? 'Update Problem' : 'Create Problem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default POTDManagementAdmin;
